import { useEffect, useState, type FormEvent, useMemo } from 'react';
import { Link, useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Sparkles,
  Coins,
  Settings2,
  FileText,
  Image as ImageIcon,
  ShoppingCart,
  HeartPulse,
  MapPin,
  Star,
  Film,
  TrendingUp,
  GraduationCap,
  Briefcase,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useCreateContent, useModels, type CreateContentInput } from '@/hooks/useContent';
import { useContentPromptTemplates } from '@/hooks/usePromptTemplates';
import { useImagePricing } from '@/hooks/useImages';
import { usePreferences } from '@/hooks/usePreferences';
import { useSites } from '@/hooks/useSites';
import { useMe } from '@/hooks/useAuth';
import { useFeatures } from '@/hooks/useFeatures';
import { ProviderModelSelect } from '@/components/ProviderModelSelect';
import { ApiError } from '@/lib/api-client';
import { formatUsd, cn } from '@/lib/utils';

const TONES = [
  'Profesyonel',
  'Samimi / Dostane',
  'Bilgilendirici',
  'İkna Edici',
  'Eğlenceli',
  'Hikaye Anlatıcı',
  'Resmi',
  'Günlük Konuşma Dili',
  'Motive Edici',
  'Eleştirel',
  'Pazarlama (İkna Edici ve Emin)',
  'Haber Formatı (Genel Haber)',
];

const AUDIENCES = [
  'Genel okuyucu',
  'Gençler',
  'Yetişkinler',
  'Öğrenciler',
  'Girişimciler',
  'Yöneticiler',
  'Teknoloji meraklıları',
  'Ev hanımları',
  'Sporcular',
  'Ebeveynler',
];

/** Per-template form customisation. Each built-in template can override
 *  the primary input's label + placeholder and add extra required fields.
 *  On submit we compose focus_keyword + custom_instructions so the
 *  existing backend prompts keep working without schema changes. */
type TemplateFieldSpec = {
  id: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  type?: 'text' | 'url';
};
type TemplateSpec = {
  primaryLabel?: string;
  primaryPlaceholder?: string;
  primaryHint?: string;
  primaryRequired?: boolean;
  extraFields?: TemplateFieldSpec[];
  hideArticleType?: boolean;
  hideFaq?: boolean;
  sectionTitle?: string;
  buildFocusKeyword?: (v: Record<string, string>) => string;
  buildInstructions?: (v: Record<string, string>, base: string) => string;
};
const TEMPLATE_SPECS: Record<string, TemplateSpec> = {
  seo_blog: {
    primaryLabel: 'Odak Anahtar Kelime',
    primaryPlaceholder: 'örn. ev taşıma ipuçları',
    primaryHint: 'Makalenin ana konusu. Başlık ve içerikte doğal şekilde kullanılır.',
  },
  how_to: {
    primaryLabel: 'Konu / Anahtar Kelime',
    primaryPlaceholder: 'örn. çamaşır makinesi nasıl temizlenir',
    primaryHint: 'AI bu konuyu adım adım anlatan bir rehber üretir.',
  },
  listicle: {
    primaryLabel: 'Liste Konusu',
    primaryPlaceholder: 'örn. 10 en iyi İstanbul restoranı',
    primaryHint: 'Sayılı bir liste yazısı. Başta sayıyı belirtmek iyi fikir.',
  },
  recipe: {
    primaryLabel: 'Tarif / Yemek Adı',
    primaryPlaceholder: 'örn. ev yapımı mercimek çorbası',
    primaryHint: 'AI malzemeler + adım adım yapılış üretir.',
    extraFields: [
      { id: 'servings', label: 'Kaç Kişilik (opsiyonel)', placeholder: 'örn. 4 kişilik' },
    ],
    buildInstructions: (v, base) =>
      [v.servings ? `Porsiyon: ${v.servings}` : '', base].filter(Boolean).join('\n\n'),
  },
  product_review: {
    primaryLabel: 'Ürün İsmi',
    primaryPlaceholder: 'örn. iPhone 15 Pro Max 256GB',
    primaryHint: 'İncelenecek ürünün tam adı. Varsa model/versiyonu da yaz.',
    extraFields: [
      { id: 'brand', label: 'Marka (opsiyonel)', placeholder: 'örn. Apple' },
      { id: 'priceHint', label: 'Fiyat Bilgisi (opsiyonel)', placeholder: 'örn. ~65.000 TL' },
    ],
    buildInstructions: (v, base) =>
      [
        v.brand ? `Marka: ${v.brand}` : '',
        v.priceHint ? `Fiyat: ${v.priceHint}` : '',
        base,
      ]
        .filter(Boolean)
        .join('\n\n'),
  },
  news_article: {
    primaryLabel: 'Haber Konusu',
    primaryPlaceholder: 'örn. Euro 2024 grup kuraları',
    primaryHint: 'Haber konusunun kısa özeti veya başlığı.',
  },
  comprehensive_guide: {
    primaryLabel: 'Rehber Konusu',
    primaryPlaceholder: 'örn. WordPress SEO rehberi',
    primaryHint: 'Kapsamlı, uzun-form rehber yazısı.',
  },
  event_announcement: {
    primaryLabel: 'Etkinlik Adı',
    primaryPlaceholder: 'örn. Ekim 2026 Web Summit',
    primaryHint: 'Tanıtılacak etkinliğin adı ve varsa kısa özeti.',
    extraFields: [
      { id: 'eventDate', label: 'Tarih (opsiyonel)', placeholder: 'örn. 15–17 Ekim 2026' },
      { id: 'eventPlace', label: 'Yer (opsiyonel)', placeholder: 'örn. İstanbul Kongre Merkezi' },
    ],
    buildInstructions: (v, base) =>
      [
        v.eventDate ? `Tarih: ${v.eventDate}` : '',
        v.eventPlace ? `Yer: ${v.eventPlace}` : '',
        base,
      ]
        .filter(Boolean)
        .join('\n\n'),
  },
  dream_interpretation: {
    primaryLabel: 'Rüya Sembolü',
    primaryPlaceholder: 'örn. rüyada beyaz at görmek',
    primaryHint: 'Yorumlanacak sembol veya rüyanın kısa açıklaması.',
  },
  match_analysis: {
    sectionTitle: 'Maç Bilgileri',
    primaryLabel: 'SEO Anahtar Kelime (opsiyonel)',
    primaryPlaceholder: 'Boş bırakırsan "TakımA vs TakımB" kullanılır',
    primaryHint: 'Boşsa takım adları kullanılır.',
    primaryRequired: false,
    hideArticleType: true,
    hideFaq: true,
    extraFields: [
      { id: 'teamA', label: 'Takım A', placeholder: 'örn. Galatasaray', required: true },
      { id: 'teamB', label: 'Takım B', placeholder: 'örn. Fenerbahçe', required: true },
      { id: 'matchDate', label: 'Maç Tarihi / Lig (opsiyonel)', placeholder: 'örn. 15 Ekim 2026 — Süper Lig' },
    ],
    buildFocusKeyword: (v) => (v.teamA && v.teamB ? `${v.teamA} vs ${v.teamB}` : ''),
    buildInstructions: (v, base) =>
      [
        `Takım A: ${v.teamA}`,
        `Takım B: ${v.teamB}`,
        v.matchDate ? `Tarih / Lig: ${v.matchDate}` : '',
        'Her iki takımın mevcut form durumunu, geçmiş karşılaşmalarını ve olası ilk 11\'leri analize dahil et. Taktiksel beklentiler ve skor tahmini ekle.',
        base,
      ]
        .filter(Boolean)
        .join('\n\n'),
  },
  comparison: {
    sectionTitle: 'Karşılaştırılan Seçenekler',
    primaryLabel: 'SEO Anahtar Kelime (opsiyonel)',
    primaryPlaceholder: 'Boş bırakırsan "X vs Y" kullanılır',
    primaryHint: 'Boşsa seçeneklerin adı kullanılır.',
    primaryRequired: false,
    hideArticleType: true,
    hideFaq: true,
    extraFields: [
      { id: 'compareX', label: 'Seçenek X', placeholder: 'örn. iPhone 15 Pro', required: true },
      { id: 'compareY', label: 'Seçenek Y', placeholder: 'örn. Samsung Galaxy S24 Ultra', required: true },
    ],
    buildFocusKeyword: (v) => (v.compareX && v.compareY ? `${v.compareX} vs ${v.compareY}` : ''),
    buildInstructions: (v, base) =>
      [
        `Karşılaştırılacak seçenek A (X): ${v.compareX}`,
        `Karşılaştırılacak seçenek B (Y): ${v.compareY}`,
        'Her iki seçeneği aynı kriterlerle değerlendir, tabloya her ikisini de ayrı sütun olarak koy, artılar/eksiler için her seçeneği ayrı alt başlıkla işle.',
        base,
      ]
        .filter(Boolean)
        .join('\n\n'),
  },
  url_rewrite: {
    sectionTitle: 'Kaynak',
    primaryLabel: 'Ana Konu / Anahtar Kelime (opsiyonel)',
    primaryPlaceholder: 'Boş bırakırsan AI kaynaktan çıkarır',
    primaryHint: 'Boşsa AI kaynak URL\'den ana konuyu çıkarır.',
    primaryRequired: false,
    hideArticleType: true,
    hideFaq: true,
    extraFields: [
      { id: 'sourceUrl', label: 'Kaynak URL', placeholder: 'https://örnek-site.com/makale', required: true, type: 'url' },
    ],
    buildFocusKeyword: () => 'URL Rewrite',
    buildInstructions: (v, base) =>
      [
        `Kaynak URL: ${v.sourceUrl}`,
        'Bu URL\'in içeriğini al, yapıyı değiştir, özgün cümlelerle plagiarism-free şekilde yeniden yaz.',
        base,
      ]
        .filter(Boolean)
        .join('\n\n'),
  },
};

// ---- New templates from migration 0013 ----

TEMPLATE_SPECS.best_of_list = {
  primaryLabel: 'Kategori',
  primaryPlaceholder: 'örn. kablosuz kulaklık',
  primaryHint: 'Listede yer alacak ürün/hizmet kategorisi.',
  extraFields: [
    { id: 'listN', label: 'Kaç Öğe', placeholder: '10' },
    { id: 'year', label: 'Yıl (opsiyonel)', placeholder: '2026' },
  ],
  buildInstructions: (v, base) =>
    [
      v.listN ? `Liste uzunluğu: En iyi ${v.listN}` : '',
      v.year ? `Yıl: ${v.year}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.buying_guide = {
  primaryLabel: 'Kategori',
  primaryPlaceholder: 'örn. laptop',
  primaryHint: 'Satın alma rehberinin odaklanacağı kategori.',
  extraFields: [
    { id: 'budget', label: 'Bütçe Aralığı (opsiyonel)', placeholder: 'örn. 20.000-40.000 TL' },
  ],
  buildInstructions: (v, base) => (v.budget ? `Bütçe: ${v.budget}\n\n${base}` : base),
};
TEMPLATE_SPECS.multi_product_compare = {
  sectionTitle: 'Karşılaştırılan Ürünler',
  primaryLabel: 'SEO Anahtar Kelime (opsiyonel)',
  primaryPlaceholder: 'Boşsa ürün adları kullanılır',
  primaryRequired: false,
  extraFields: [
    { id: 'p1', label: 'Ürün 1', required: true, placeholder: 'örn. iPhone 15' },
    { id: 'p2', label: 'Ürün 2', required: true, placeholder: 'örn. Galaxy S24' },
    { id: 'p3', label: 'Ürün 3', placeholder: 'örn. Pixel 9 (ops.)' },
    { id: 'p4', label: 'Ürün 4', placeholder: 'opsiyonel' },
    { id: 'p5', label: 'Ürün 5', placeholder: 'opsiyonel' },
  ],
  buildFocusKeyword: (v) => [v.p1, v.p2, v.p3, v.p4, v.p5].filter(Boolean).join(' vs '),
  buildInstructions: (v, base) => {
    const list = [v.p1, v.p2, v.p3, v.p4, v.p5].filter(Boolean);
    return [
      `Karşılaştırılacak ürünler (${list.length} adet): ${list.join(' | ')}`,
      'Tüm ürünleri aynı kriterlerle yan yana değerlendir. Karşılaştırma tablosunda her ürün ayrı sütun olsun. Her ürün için ayrı artı/eksi alt başlığı ekle. Son bölümde net kazanan + runner-up belirt.',
      base,
    ]
      .filter(Boolean)
      .join('\n\n');
  },
};
TEMPLATE_SPECS.alternatives = {
  primaryLabel: 'Ana Ürün / Hizmet',
  primaryPlaceholder: 'örn. Netflix',
  primaryHint: 'Alternatif aranacak ana ürün/hizmet.',
};
TEMPLATE_SPECS.health_symptom = {
  primaryLabel: 'Hastalık / Semptom',
  primaryPlaceholder: 'örn. migren ağrısı',
  primaryHint: 'AI otomatik olarak "bu yazı tanı/tedavi değildir" uyarısı ekler.',
};
TEMPLATE_SPECS.diet_plan = {
  primaryLabel: 'Diyet Adı',
  primaryPlaceholder: 'örn. Akdeniz diyeti',
  extraFields: [
    { id: 'duration', label: 'Süre (opsiyonel)', placeholder: 'örn. 7 gün / 30 gün' },
    { id: 'calorie', label: 'Kalori Hedefi (opsiyonel)', placeholder: 'örn. 1500 kcal/gün' },
  ],
  buildInstructions: (v, base) =>
    [
      v.duration ? `Süre: ${v.duration}` : '',
      v.calorie ? `Kalori hedefi: ${v.calorie}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.workout_plan = {
  primaryLabel: 'Antrenman',
  primaryPlaceholder: 'örn. başlangıç vücut geliştirme',
  extraFields: [
    { id: 'level', label: 'Seviye (opsiyonel)', placeholder: 'örn. başlangıç / orta / ileri' },
    { id: 'duration', label: 'Süre (opsiyonel)', placeholder: 'örn. 30 gün' },
  ],
  buildInstructions: (v, base) =>
    [
      v.level ? `Seviye: ${v.level}` : '',
      v.duration ? `Süre: ${v.duration}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.supplement_review = {
  primaryLabel: 'Supplement',
  primaryPlaceholder: 'örn. kreatin monohidrat',
  extraFields: [
    { id: 'brand', label: 'Marka (opsiyonel)', placeholder: 'örn. Optimum Nutrition' },
  ],
  buildInstructions: (v, base) => (v.brand ? `Marka: ${v.brand}\n\n${base}` : base),
};
TEMPLATE_SPECS.city_guide = {
  primaryLabel: 'Şehir / Destinasyon',
  primaryPlaceholder: 'örn. Lizbon',
  extraFields: [
    { id: 'duration', label: 'Süre', placeholder: 'örn. 3 gün / 1 hafta' },
    { id: 'budget', label: 'Bütçe (opsiyonel)', placeholder: 'örn. ekonomik / orta / lüks' },
  ],
  buildInstructions: (v, base) =>
    [
      v.duration ? `Süre: ${v.duration}` : '',
      v.budget ? `Bütçe: ${v.budget}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.places_list = {
  primaryLabel: 'Bölge / Şehir',
  primaryPlaceholder: 'örn. Kapadokya',
  extraFields: [
    { id: 'theme', label: 'Tema (opsiyonel)', placeholder: 'örn. doğa, tarih, fotoğraf' },
  ],
  buildInstructions: (v, base) => (v.theme ? `Tema: ${v.theme}\n\n${base}` : base),
};
TEMPLATE_SPECS.hotel_review = {
  primaryLabel: 'Otel Adı',
  primaryPlaceholder: 'örn. Rixos Downtown Antalya',
  extraFields: [
    { id: 'city', label: 'Şehir', placeholder: 'örn. Antalya' },
    { id: 'price', label: 'Fiyat (opsiyonel)', placeholder: 'örn. 3.500 TL/gece' },
  ],
  buildInstructions: (v, base) =>
    [
      v.city ? `Şehir: ${v.city}` : '',
      v.price ? `Fiyat bilgisi: ${v.price}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.restaurant_review = {
  primaryLabel: 'Mekan Adı',
  primaryPlaceholder: 'örn. Mikla',
  extraFields: [
    { id: 'city', label: 'Şehir', placeholder: 'örn. İstanbul' },
    { id: 'cuisine', label: 'Mutfak Türü (opsiyonel)', placeholder: 'örn. modern Türk' },
  ],
  buildInstructions: (v, base) =>
    [
      v.city ? `Şehir: ${v.city}` : '',
      v.cuisine ? `Mutfak: ${v.cuisine}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.skincare_routine = {
  primaryLabel: 'Rutin / Hedef',
  primaryPlaceholder: 'örn. karma cilt için sabah rutini',
  extraFields: [
    { id: 'skinType', label: 'Cilt / Saç Tipi (opsiyonel)', placeholder: 'örn. karma, yağlı, kuru' },
  ],
  buildInstructions: (v, base) => (v.skinType ? `Cilt/saç tipi: ${v.skinType}\n\n${base}` : base),
};
TEMPLATE_SPECS.makeup_tutorial = {
  primaryLabel: 'Look Adı',
  primaryPlaceholder: 'örn. gündüz doğal makyaj',
  extraFields: [
    { id: 'skinType', label: 'Cilt Tipi (opsiyonel)', placeholder: 'örn. yağlı / kuru' },
    { id: 'level', label: 'Seviye (opsiyonel)', placeholder: 'başlangıç / orta / ileri' },
  ],
  buildInstructions: (v, base) =>
    [
      v.skinType ? `Cilt tipi: ${v.skinType}` : '',
      v.level ? `Seviye: ${v.level}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.fashion_trend = {
  primaryLabel: 'Trend / Sezon',
  primaryPlaceholder: 'örn. 2026 yaz moda trendleri',
};
TEMPLATE_SPECS.zodiac_horoscope = {
  primaryLabel: 'Burç ve Dönem',
  primaryPlaceholder: 'örn. Koç — günlük / haftalık',
  extraFields: [
    { id: 'sign', label: 'Burç', required: true, placeholder: 'örn. Koç' },
    { id: 'period', label: 'Dönem', required: true, placeholder: 'günlük / haftalık / aylık' },
    { id: 'date', label: 'Tarih (opsiyonel)', placeholder: 'örn. 20 Nisan 2026' },
  ],
  primaryRequired: false,
  buildFocusKeyword: (v) => (v.sign && v.period ? `${v.sign} ${v.period} burç yorumu` : ''),
  buildInstructions: (v, base) =>
    [
      `Burç: ${v.sign}`,
      `Dönem: ${v.period}`,
      v.date ? `Tarih: ${v.date}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.tarot_card = {
  primaryLabel: 'Kart',
  primaryPlaceholder: 'örn. The Fool',
  extraFields: [
    { id: 'orientation', label: 'Pozisyon (opsiyonel)', placeholder: 'düz / ters / ikisi de' },
  ],
  buildInstructions: (v, base) =>
    v.orientation ? `Pozisyon: ${v.orientation}\n\n${base}` : base,
};
TEMPLATE_SPECS.numerology = {
  primaryLabel: 'Sayı / Hesap',
  primaryPlaceholder: 'örn. yaşam yolu sayısı 7 / isim: Ahmet',
};
TEMPLATE_SPECS.name_meaning = {
  primaryLabel: 'İsim',
  primaryPlaceholder: 'örn. Zeynep',
  primaryHint: 'Anlamı, kökeni ve karakter analizi üretilir.',
};
TEMPLATE_SPECS.movie_show_review = {
  primaryLabel: 'Film / Dizi',
  primaryPlaceholder: 'örn. Dune Part Two (2024)',
  extraFields: [
    { id: 'year', label: 'Yıl (opsiyonel)', placeholder: '2024' },
    { id: 'platform', label: 'Platform (opsiyonel)', placeholder: 'Netflix / Sinema' },
  ],
  buildInstructions: (v, base) =>
    [
      v.year ? `Yıl: ${v.year}` : '',
      v.platform ? `Platform: ${v.platform}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.episode_recap = {
  primaryLabel: 'Dizi',
  primaryPlaceholder: 'örn. The Last of Us',
  extraFields: [
    { id: 'season', label: 'Sezon', required: true, placeholder: 'örn. 2' },
    { id: 'episode', label: 'Bölüm', required: true, placeholder: 'örn. 5' },
  ],
  buildFocusKeyword: (v) => (v.season && v.episode ? `S${v.season}E${v.episode}` : ''),
  buildInstructions: (v, base) =>
    [`Sezon: ${v.season}`, `Bölüm: ${v.episode}`, base].filter(Boolean).join('\n\n'),
};
TEMPLATE_SPECS.game_review = {
  primaryLabel: 'Oyun Adı',
  primaryPlaceholder: 'örn. Elden Ring',
  extraFields: [
    { id: 'platform', label: 'Platform (opsiyonel)', placeholder: 'PC / PS5 / Xbox' },
    { id: 'genre', label: 'Tür (opsiyonel)', placeholder: 'RPG, FPS, vs.' },
  ],
  buildInstructions: (v, base) =>
    [
      v.platform ? `Platform: ${v.platform}` : '',
      v.genre ? `Tür: ${v.genre}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.character_analysis = {
  primaryLabel: 'Karakter',
  primaryPlaceholder: 'örn. Walter White',
  extraFields: [
    { id: 'work', label: 'Yapım', required: true, placeholder: 'örn. Breaking Bad' },
  ],
  buildFocusKeyword: (v) => (v.work ? `${v.work} — karakter` : ''),
  buildInstructions: (v, base) => (v.work ? `Yapım: ${v.work}\n\n${base}` : base),
};
TEMPLATE_SPECS.crypto_stock_analysis = {
  primaryLabel: 'Varlık',
  primaryPlaceholder: 'örn. Bitcoin / THYAO',
  extraFields: [
    { id: 'timeframe', label: 'Zaman Dilimi (opsiyonel)', placeholder: 'kısa / orta / uzun vadeli' },
  ],
  buildInstructions: (v, base) =>
    v.timeframe ? `Zaman dilimi: ${v.timeframe}\n\n${base}` : base,
};
TEMPLATE_SPECS.tax_guide = {
  primaryLabel: 'Vergi / Konu',
  primaryPlaceholder: 'örn. 2026 KDV beyannamesi',
  extraFields: [
    { id: 'year', label: 'Yıl (opsiyonel)', placeholder: '2026' },
  ],
  buildInstructions: (v, base) => (v.year ? `Vergi yılı: ${v.year}\n\n${base}` : base),
};
TEMPLATE_SPECS.investment_guide = {
  primaryLabel: 'Yatırım Konusu',
  primaryPlaceholder: 'örn. altın yatırımı',
  extraFields: [
    { id: 'risk', label: 'Risk Profili (opsiyonel)', placeholder: 'düşük / orta / yüksek' },
  ],
  buildInstructions: (v, base) => (v.risk ? `Risk profili: ${v.risk}\n\n${base}` : base),
};
TEMPLATE_SPECS.topic_explanation = {
  primaryLabel: 'Konu',
  primaryPlaceholder: 'örn. 8. sınıf hücre konusu',
  extraFields: [
    { id: 'level', label: 'Sınıf / Seviye (opsiyonel)', placeholder: 'örn. 8. sınıf' },
  ],
  buildInstructions: (v, base) => (v.level ? `Seviye: ${v.level}\n\n${base}` : base),
};
TEMPLATE_SPECS.biography = {
  primaryLabel: 'Kişi',
  primaryPlaceholder: 'örn. Mustafa Kemal Atatürk',
  extraFields: [
    { id: 'focus', label: 'Odak (opsiyonel)', placeholder: 'örn. askeri kariyer / kişisel hayat' },
  ],
  buildInstructions: (v, base) => (v.focus ? `Odak: ${v.focus}\n\n${base}` : base),
};
TEMPLATE_SPECS.book_summary = {
  primaryLabel: 'Kitap ve Yazar',
  primaryPlaceholder: 'örn. Sapiens — Yuval Noah Harari',
  extraFields: [
    { id: 'author', label: 'Yazar (opsiyonel)', placeholder: 'örn. Yuval Noah Harari' },
  ],
  buildInstructions: (v, base) => (v.author ? `Yazar: ${v.author}\n\n${base}` : base),
};
TEMPLATE_SPECS.historical_event = {
  primaryLabel: 'Olay',
  primaryPlaceholder: 'örn. İstanbul\'un Fethi',
  extraFields: [
    { id: 'year', label: 'Yıl (opsiyonel)', placeholder: 'örn. 1453' },
  ],
  buildInstructions: (v, base) => (v.year ? `Yıl: ${v.year}\n\n${base}` : base),
};
TEMPLATE_SPECS.press_release = {
  primaryLabel: 'Duyuru Konusu',
  primaryPlaceholder: 'örn. yeni ürün lansmanı',
  extraFields: [
    { id: 'company', label: 'Şirket', required: true, placeholder: 'örn. Acme A.Ş.' },
    { id: 'date', label: 'Tarih (opsiyonel)', placeholder: 'örn. 20 Nisan 2026' },
  ],
  buildInstructions: (v, base) =>
    [`Şirket: ${v.company}`, v.date ? `Tarih: ${v.date}` : '', base]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.interview = {
  primaryLabel: 'Konuk ve Konu',
  primaryPlaceholder: 'örn. Dr. X — AI etiği',
  extraFields: [
    { id: 'guestName', label: 'Konuk İsim', required: true, placeholder: 'örn. Dr. Ahmet X' },
    { id: 'guestTitle', label: 'Unvan (opsiyonel)', placeholder: 'örn. Etik Danışmanı' },
    { id: 'topics', label: 'Ana Konular (opsiyonel)', placeholder: 'virgülle ayrılmış' },
  ],
  buildInstructions: (v, base) =>
    [
      `Konuk: ${v.guestName}`,
      v.guestTitle ? `Unvan: ${v.guestTitle}` : '',
      v.topics ? `Konular: ${v.topics}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.case_study = {
  primaryLabel: 'Firma ve Konu',
  primaryPlaceholder: 'örn. Acme — müşteri kazanımı',
  extraFields: [
    { id: 'sector', label: 'Sektör (opsiyonel)', placeholder: 'örn. e-ticaret' },
    { id: 'result', label: 'Sonuç / Metrik (opsiyonel)', placeholder: 'örn. +%40 dönüşüm' },
  ],
  buildInstructions: (v, base) =>
    [
      v.sector ? `Sektör: ${v.sector}` : '',
      v.result ? `Ana sonuç: ${v.result}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.text_to_blog = {
  sectionTitle: 'Kaynak Metin',
  primaryLabel: 'Ana Konu (opsiyonel)',
  primaryPlaceholder: 'Boşsa AI metinden çıkarır',
  primaryRequired: false,
  extraFields: [
    {
      id: 'sourceText',
      label: 'Kaynak Metin (PDF/doc içeriğini buraya yapıştır)',
      required: true,
      placeholder: 'Metni buraya yapıştır…',
    },
  ],
  buildFocusKeyword: () => 'Metin → Blog',
  buildInstructions: (v, base) =>
    [
      `Kaynak metin:\n${v.sourceText}`,
      'Bu metni bilgi kaybetmeden akıcı ve SEO uyumlu bir blog postuna dönüştür.',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.youtube_to_blog = {
  sectionTitle: 'Kaynak',
  primaryLabel: 'Video Konusu',
  primaryPlaceholder: 'örn. WordPress SEO rehberi',
  extraFields: [
    { id: 'videoUrl', label: 'YouTube URL', required: true, type: 'url', placeholder: 'https://youtube.com/...' },
    { id: 'transcript', label: 'Transkript (opsiyonel)', placeholder: 'Elindeyse yapıştır, daha iyi çeviri için' },
  ],
  buildInstructions: (v, base) =>
    [
      `Video URL: ${v.videoUrl}`,
      v.transcript ? `Transkript:\n${v.transcript}` : '',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};
TEMPLATE_SPECS.summarize = {
  sectionTitle: 'Özetlenecek Kaynak',
  primaryLabel: 'Konu / Başlık',
  primaryPlaceholder: 'örn. 2026 ekonomi raporu özeti',
  extraFields: [
    {
      id: 'sourceText',
      label: 'Kaynak Metin veya URL',
      required: true,
      placeholder: 'Metni yapıştır veya URL yaz',
    },
  ],
  buildInstructions: (v, base) =>
    [
      v.sourceText ? `Kaynak: ${v.sourceText}` : '',
      'Ana tezi koru, sadece kritik bilgiyi ilet, atıl cümleleri at.',
      base,
    ]
      .filter(Boolean)
      .join('\n\n'),
};

// Category → label + icon. Shown as pills at the top of the picker;
// clicking one filters the template dropdown to that vertical.
type CategoryMeta = { id: string; label: string; icon: LucideIcon };
const CATEGORY_META: CategoryMeta[] = [
  { id: 'ecommerce', label: 'E-ticaret / Affiliate', icon: ShoppingCart },
  { id: 'health', label: 'Sağlık / Yaşam', icon: HeartPulse },
  { id: 'travel', label: 'Seyahat / Lokasyon', icon: MapPin },
  { id: 'lifestyle', label: 'Güzellik / Moda', icon: Sparkles },
  { id: 'spiritual', label: 'Mistik', icon: Star },
  { id: 'entertainment', label: 'Eğlence / Medya', icon: Film },
  { id: 'finance', label: 'Finans / Hukuk', icon: TrendingUp },
  { id: 'education', label: 'Eğitim', icon: GraduationCap },
  { id: 'business', label: 'Kurumsal / B2B', icon: Briefcase },
  { id: 'utility', label: 'Utility', icon: Wrench },
  { id: 'blog', label: 'Blog / Genel', icon: FileText },
];

const DEFAULT_SPEC: TemplateSpec = {
  primaryLabel: 'Odak Anahtar Kelime',
  primaryPlaceholder: 'örn. ev taşıma ipuçları',
  primaryHint: 'Makalenin ana konusu.',
};

export function ContentNewPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const createMut = useCreateContent();
  const sites = useSites();
  const models = useModels();
  const prefs = usePreferences();
  const me = useMe();
  const features = useFeatures();
  const [webGrounded, setWebGrounded] = useState(false);
  // Dynamic extra fields keyed by TEMPLATE_SPECS[...].extraFields[].id.
  // Cleared when the template changes so old values don't leak across
  // unrelated templates.
  const [extra, setExtra] = useState<Record<string, string>>({});

  const activeOrg = me.data?.organizations.find((o) => o.id === me.data?.active_org_id);

  const [form, setForm] = useState<CreateContentInput>({
    focus_keyword: '',
    title: '',
    language: 'Türkçe',
    tone: 'Profesyonel',
    audience: 'Genel okuyucu',
    article_type: 'general',
    template_type: 'seo_blog',
    section_length: 'medium',
    include_faq: false,
    site_id: null,
  });

  const templates = useContentPromptTemplates();
  const imagePricing = useImagePricing();

  // Image options state (not part of content creation, only passed to generate endpoint)
  const [imgOpts, setImgOpts] = useState({
    enable_featured: false,
    enable_body: false,
    body_count: 2 as 1 | 2 | 3 | 4 | 5,
    body_layout: 'full_width' as 'full_width' | 'float_left' | 'float_right' | 'alternating',
    pricing_id: 'gemini-2-5-flash-image',
  });
  const selectedImagePricing = useMemo(
    () => imagePricing.data?.pricing.find((x) => x.id === imgOpts.pricing_id),
    [imagePricing.data, imgOpts.pricing_id],
  );
  const imageCreditsPerImage = selectedImagePricing?.credits ?? 0;
  const IMG_FAMILY_LABEL: Record<string, string> = {
    flux: 'Black Forest Labs (Flux)',
    'dall-e': 'OpenAI (DALL-E + GPT Image)',
    imagen: 'Google (Imagen)',
    'gemini-image': 'Google (Gemini Image / Nano Banana)',
    'stable-diffusion': 'Stability AI (Stable Diffusion)',
    seedream: 'ByteDance (Seedream)',
    wan: 'Alibaba (Wan)',
    'qwen-image': 'Alibaba (Qwen Image / Z-Image)',
    'grok-image': 'xAI (Grok Imagine)',
    recraft: 'Recraft',
    reve: 'Reve',
    kling: 'Kling AI',
    tencent: 'Tencent (Hunyuan)',
    other: 'Diğer',
  };
  const IMG_FAMILY_ORDER = [
    'flux',
    'dall-e',
    'imagen',
    'gemini-image',
    'stable-diffusion',
    'seedream',
    'wan',
    'qwen-image',
    'grok-image',
    'recraft',
    'reve',
    'kling',
    'tencent',
    'other',
  ];
  const imagePricingGroups = useMemo(() => {
    if (!imagePricing.data) return [];
    const g: Record<string, typeof imagePricing.data.pricing> = {};
    for (const p of imagePricing.data.pricing) {
      (g[p.family] = g[p.family] ?? []).push(p);
    }
    return IMG_FAMILY_ORDER.filter((f) => g[f]?.length).map((id) => ({
      id,
      label: IMG_FAMILY_LABEL[id] ?? id,
      items: (g[id] ?? []).map((p) => ({
        id: p.id,
        label: p.label,
        suffix: `$${(p.credits / 1_000_000).toFixed(2)}${p.note ? ` · ${p.note}` : ''}`,
        deprecated: p.deprecated,
      })),
    }));
  }, [imagePricing.data]);
  const totalImageCredits =
    (imgOpts.enable_featured ? imageCreditsPerImage : 0) +
    (imgOpts.enable_body ? imageCreditsPerImage * imgOpts.body_count : 0);

  // Chat-model picker groups, keyed by provider family.
  const CHAT_FAMILY_LABEL: Record<string, string> = {
    openai: 'OpenAI',
    anthropic: 'Anthropic Claude',
    gemini: 'Google Gemini',
    xai: 'xAI Grok',
    deepseek: 'DeepSeek',
    meta: 'Meta Llama',
    mistral: 'Mistral',
    qwen: 'Qwen (Alibaba)',
    minimax: 'MiniMax',
    glm: 'Zhipu GLM',
    kimi: 'Moonshot Kimi',
    ernie: 'Baidu ERNIE',
    perplexity: 'Perplexity Sonar',
    cohere: 'Cohere',
    nvidia: 'NVIDIA',
    other: 'Diğer',
  };
  const CHAT_FAMILY_ORDER = [
    'openai', 'anthropic', 'gemini', 'xai', 'deepseek', 'meta', 'mistral',
    'qwen', 'minimax', 'glm', 'kimi', 'ernie', 'perplexity', 'cohere', 'nvidia', 'other',
  ];
  const chatModelGroups = useMemo(() => {
    if (!models.data) return [];
    const g: Record<string, typeof models.data.models> = {};
    for (const m of models.data.models) (g[m.family] = g[m.family] ?? []).push(m);
    return CHAT_FAMILY_ORDER.filter((f) => g[f]?.length).map((id) => ({
      id,
      label: CHAT_FAMILY_LABEL[id] ?? id,
      items: (g[id] ?? []).map((m) => ({
        id: m.id,
        label: m.label,
        suffix: `$${(m.credits_per_word / 1_000_000).toFixed(6)}/kelime${m.note ? ` · ${m.note}` : ''}`,
        deprecated: false,
      })),
    }));
  }, [models.data]);
  const selectedTemplate = templates.data?.templates.find((t) => t.id === form.template_type);
  const spec = TEMPLATE_SPECS[form.template_type ?? 'seo_blog'] ?? DEFAULT_SPEC;
  const [selectedCategory, setSelectedCategory] = useState<string>(
    selectedTemplate?.category ?? 'blog',
  );
  // When the selected template's category changes (e.g. from loading
  // preferences), keep the pill in sync so the dropdown lists the right
  // templates.
  useEffect(() => {
    if (selectedTemplate?.category && selectedTemplate.category !== selectedCategory) {
      setSelectedCategory(selectedTemplate.category);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate?.id]);
  const templatesInCategory = useMemo(
    () => (templates.data?.templates ?? []).filter((t) => (t.category ?? 'blog') === selectedCategory),
    [templates.data, selectedCategory],
  );
  const categoryCounts = useMemo(() => {
    const m: Record<string, number> = {};
    for (const t of templates.data?.templates ?? []) m[t.category ?? 'blog'] = (m[t.category ?? 'blog'] ?? 0) + 1;
    return m;
  }, [templates.data]);
  const [error, setError] = useState<string | null>(null);
  const [generating, setGenerating] = useState(false);

  // Hydrate from user preferences when loaded
  useEffect(() => {
    if (!prefs.data?.preferences) return;
    const p = prefs.data.preferences;
    setForm((prev) => ({
      ...prev,
      language: p.default_language ?? prev.language,
      tone: p.default_tone ?? prev.tone,
      audience: p.default_audience ?? prev.audience,
      article_type: p.default_article_type ?? prev.article_type,
      section_length: p.default_section_length ?? prev.section_length,
    }));
  }, [prefs.data]);

  // Local model override so the user can switch on the fly without
  // leaving /content/new. Initialized from their saved default but
  // becomes independent after a change here; preferences aren't
  // mutated unless they explicitly save from the Preferences page.
  const [selectedModelId, setSelectedModelId] = useState<string>('gpt-4o-mini');
  useEffect(() => {
    const d = prefs.data?.preferences.default_ai_model;
    if (d) setSelectedModelId(d);
  }, [prefs.data]);
  const selectedModel = useMemo(
    () => models.data?.models.find((m) => m.id === selectedModelId),
    [models.data, selectedModelId],
  );
  const selectedLength = useMemo(
    () => models.data?.length_presets.find((l) => l.id === form.section_length),
    [models.data, form.section_length],
  );
  const estimateAvg = selectedModel && selectedLength ? selectedModel.credits_per_word * selectedLength.target : 0;
  const estimateMax = selectedModel && selectedLength ? selectedModel.credits_per_word * selectedLength.max : 0;

  const update = <K extends keyof CreateContentInput>(key: K, value: CreateContentInput[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // When user clicks a category pill, switch the category and auto-pick
  // the first template in it so the form stays filled in.
  const pickCategory = (cat: string) => {
    if (cat === selectedCategory) return;
    setSelectedCategory(cat);
    const first = (templates.data?.templates ?? []).find((t) => (t.category ?? 'blog') === cat);
    if (first) pickTemplate(first.id);
  };

  // When user picks a template, merge its default settings into the form
  const pickTemplate = (id: string) => {
    const t = templates.data?.templates.find((x) => x.id === id);
    // Clear extra-field state whenever the template changes so prior
    // values (source URL / teams / X-Y) don't leak into unrelated templates.
    setExtra({});
    setForm((prev) => {
      const next: CreateContentInput = { ...prev, template_type: id };
      if (t?.default_settings_json) {
        try {
          const defaults = JSON.parse(t.default_settings_json) as Partial<CreateContentInput>;
          if (defaults.tone) next.tone = defaults.tone;
          if (defaults.audience) next.audience = defaults.audience;
          if (defaults.section_length) next.section_length = defaults.section_length as typeof next.section_length;
          if (typeof defaults.include_faq === 'boolean') next.include_faq = defaults.include_faq;
        } catch {
          /* ignore invalid JSON */
        }
      }
      return next;
    });
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      // Per-template tweaks: compose the right focus_keyword and
      // funnel extra fields (URL, X/Y, teams) through custom_instructions
      // so existing prompts keep working without backend changes.
      const submitForm = { ...form };
      const base = form.custom_instructions ?? '';
      const trimmed: Record<string, string> = {};
      for (const [k, v] of Object.entries(extra)) trimmed[k] = v.trim();
      const composedKw = spec.buildFocusKeyword?.(trimmed);
      if (composedKw && !submitForm.focus_keyword) submitForm.focus_keyword = composedKw;
      const finalInstructions = spec.buildInstructions
        ? spec.buildInstructions(trimmed, base)
        : base;
      const { content } = await createMut.mutateAsync(submitForm);
      setGenerating(true);
      const res = await fetch(`/api/app/content/${content.id}/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ai_model: selectedModelId,
          include_faq: form.include_faq,
          include_meta: true,
          custom_instructions: finalInstructions || undefined,
          web_grounded: webGrounded && features.web_grounding,
          image_options:
            imgOpts.enable_featured || imgOpts.enable_body
              ? {
                  featured: imgOpts.enable_featured,
                  body_count: imgOpts.enable_body ? imgOpts.body_count : 0,
                  body_layout: imgOpts.body_layout,
                  pricing_id: imgOpts.pricing_id,
                }
              : undefined,
        }),
      });
      const body = (await res.json()) as { ok: boolean; error?: { message: string } };
      if (!body.ok) {
        throw new ApiError(body.error?.message ?? 'Üretim hatası', 'generation_failed', res.status);
      }
      // Refresh credit balance + content caches so the new balance shows up
      // everywhere (dashboard meter, detail page "Harcanan" badge).
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['me'] }),
        qc.invalidateQueries({ queryKey: ['content'] }),
      ]);
      navigate({ to: '/content/$id', params: { id: content.id } });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'İçerik oluşturulamadı');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="max-w-4xl space-y-6">
      <div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/content">
            <ArrowLeft className="w-4 h-4" /> İçeriklere dön
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold mt-2">Yeni İçerik Oluştur</h1>
        <p className="text-muted-foreground text-sm">
          AI ile Türkçe blog makalesi üret. Varsayılan model ve tercihlerini{' '}
          <Link to="/account/preferences" className="text-primary underline-offset-4 hover:underline">
            Tercihlerim
          </Link>
          {' '}sayfasından değiştirebilirsin.
        </p>
      </div>

      <form onSubmit={onSubmit} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <FileText className="w-4 h-4" /> İçerik Türü
            </CardTitle>
            <CardDescription>Hangi tür içerik üreteceğini seç — her türün kendine özgü prompt yapısı var.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {templates.isPending && <div className="text-sm text-muted-foreground">Şablonlar yükleniyor…</div>}
            {templates.data && templates.data.templates.length > 0 && (
              <>
                {/* Category pills — pick a vertical first, templates below
                    are filtered to match. Categories without any templates
                    are hidden so admins can delete verticals cleanly. */}
                <div className="flex flex-wrap gap-2">
                  {CATEGORY_META.filter((c) => (categoryCounts[c.id] ?? 0) > 0).map((c) => {
                    const Icon = c.icon;
                    const active = selectedCategory === c.id;
                    return (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => pickCategory(c.id)}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                          active
                            ? 'border-primary bg-primary text-primary-foreground'
                            : 'border-border bg-background hover:bg-accent',
                        )}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {c.label}
                        <span
                          className={cn(
                            'ml-0.5 rounded-full px-1.5 py-0 text-[10px]',
                            active ? 'bg-primary-foreground/20' : 'bg-muted text-muted-foreground',
                          )}
                        >
                          {categoryCounts[c.id] ?? 0}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <select
                  value={form.template_type ?? ''}
                  onChange={(e) => pickTemplate(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {templatesInCategory.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.label}
                    </option>
                  ))}
                </select>

                {selectedTemplate && (
                  <div className="rounded-md border bg-muted/30 p-3 text-sm space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <strong>{selectedTemplate.label}</strong>
                      {selectedTemplate.has_image_prompt ? (
                        <span className="text-xs text-primary bg-primary/10 px-2 py-0.5 rounded inline-flex items-center gap-1">
                          <ImageIcon className="w-3 h-3" />
                          Görsel promptu hazır
                        </span>
                      ) : null}
                    </div>
                    {selectedTemplate.description && (
                      <p className="text-muted-foreground text-xs leading-relaxed">{selectedTemplate.description}</p>
                    )}
                  </div>
                )}
              </>
            )}
            {templates.data && templates.data.templates.length === 0 && (
              <div className="text-sm text-muted-foreground">
                Henüz şablon yok — varsayılan SEO Blog kullanılacak.
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">{spec.sectionTitle ?? 'Temel Bilgiler'}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {spec.extraFields && spec.extraFields.length > 0 && (
              <div
                className={cn(
                  'grid gap-4',
                  spec.extraFields.length === 2 && 'sm:grid-cols-2',
                  spec.extraFields.length >= 3 && 'sm:grid-cols-3',
                )}
              >
                {spec.extraFields.map((f) => (
                  <div key={f.id} className="space-y-2">
                    <Label htmlFor={`extra-${f.id}`}>
                      {f.label}
                      {f.required && <span className="text-destructive"> *</span>}
                    </Label>
                    <Input
                      id={`extra-${f.id}`}
                      type={f.type ?? 'text'}
                      required={!!f.required}
                      value={extra[f.id] ?? ''}
                      onChange={(e) => setExtra((prev) => ({ ...prev, [f.id]: e.target.value }))}
                      placeholder={f.placeholder}
                    />
                  </div>
                ))}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="keyword">
                {spec.primaryLabel ?? 'Odak Anahtar Kelime'}
                {spec.primaryRequired !== false && <span className="text-destructive"> *</span>}
              </Label>
              <Input
                id="keyword"
                required={spec.primaryRequired !== false}
                value={form.focus_keyword}
                onChange={(e) => update('focus_keyword', e.target.value)}
                placeholder={spec.primaryPlaceholder}
              />
              {spec.primaryHint && (
                <p className="text-xs text-muted-foreground">{spec.primaryHint}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="title">Başlık (opsiyonel)</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => update('title', e.target.value)}
                placeholder="Boş bırakırsan AI kendisi önerir"
              />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="tone">Yazı Stili / Tonu</Label>
                <select
                  id="tone"
                  value={form.tone}
                  onChange={(e) => update('tone', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {TONES.map((t) => (
                    <option key={t} value={t}>
                      {t}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="audience">Hedef Kitle</Label>
                <select
                  id="audience"
                  value={form.audience}
                  onChange={(e) => update('audience', e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {AUDIENCES.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {!spec.hideArticleType && (
                <div className="space-y-2">
                  <Label htmlFor="type">Format</Label>
                  <select
                    id="type"
                    value={form.article_type}
                    onChange={(e) => update('article_type', e.target.value as CreateContentInput['article_type'])}
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                  >
                    <option value="general">Genel makale</option>
                    <option value="how_to">Nasıl yapılır (adım adım)</option>
                    <option value="list">Liste yazısı</option>
                  </select>
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="length">Uzunluk</Label>
                <select
                  id="length"
                  value={form.section_length}
                  onChange={(e) =>
                    update('section_length', e.target.value as CreateContentInput['section_length'])
                  }
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
                >
                  {models.data?.length_presets.map((l) => {
                    const locked = l.id === 'long' && !features.long_articles;
                    return (
                      <option key={l.id} value={l.id} disabled={locked}>
                        {l.label}
                        {locked ? ' — Uzun Makale eklentisi gerekli' : ''}
                      </option>
                    );
                  })}
                </select>
                {!features.long_articles && form.section_length === 'long' && (
                  <p className="text-xs text-destructive">
                    Uzun makale eklentin yok. <Link to="/packages" className="underline">Eklentiyi al</Link>.
                  </p>
                )}
              </div>
            </div>
            {!spec.hideFaq && (
              <div className="flex items-center gap-2">
                <input
                  id="faq"
                  type="checkbox"
                  checked={!!form.include_faq}
                  onChange={(e) => update('include_faq', e.target.checked)}
                  className="h-4 w-4 rounded border-input"
                />
                <Label htmlFor="faq" className="cursor-pointer">
                  Sık Sorulan Sorular bölümü ekle
                </Label>
              </div>
            )}
            <div className="flex items-start gap-2">
              <input
                id="web-grounded"
                type="checkbox"
                checked={webGrounded}
                onChange={(e) => setWebGrounded(e.target.checked)}
                disabled={!features.web_grounding}
                className="h-4 w-4 rounded border-input mt-0.5"
              />
              <div>
                <Label
                  htmlFor="web-grounded"
                  className={cn(
                    'cursor-pointer',
                    !features.web_grounding && 'text-muted-foreground cursor-not-allowed',
                  )}
                >
                  Güncel bilgi kullan{' '}
                  <span className="text-xs text-muted-foreground">(web grounding · 4× bakiye)</span>
                </Label>
                {!features.web_grounding ? (
                  <p className="text-xs text-destructive">
                    Güncel Bilgi eklentin yok.{' '}
                    <Link to="/packages" className="underline">Eklentiyi al</Link>.
                  </p>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    AI, canlı web araması yapar — haber/istatistik/son gelişme içeren yazılarda faydalı.
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <CardTitle className="text-base">AI Modeli</CardTitle>
                <CardDescription>
                  {selectedModel ? (
                    <>
                      <span className="font-mono">${(selectedModel.credits_per_word / 1_000_000).toFixed(6)}/kelime</span>
                      {selectedModel.note ? ` · ${selectedModel.note}` : ''}
                    </>
                  ) : (
                    'Bir model seç'
                  )}
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/account/preferences">
                  <Settings2 className="w-4 h-4" /> Varsayılan
                </Link>
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            <ProviderModelSelect
              groups={chatModelGroups}
              value={selectedModelId}
              onChange={setSelectedModelId}
              providerLabel="Sağlayıcı"
              modelLabel="Model"
            />
            {selectedLength && selectedModel && (
              <div className="rounded-md border bg-muted/30 px-3 py-2 text-sm flex items-center gap-2 flex-wrap">
                <Coins className="w-4 h-4 text-primary" />
                <div className="flex-1 min-w-[200px]">
                  Tahmini metin: <strong className="font-mono">{formatUsd(estimateAvg)}</strong>{' '}
                  (ortalama), en fazla <strong className="font-mono">{formatUsd(estimateMax)}</strong>
                </div>
                <div className="text-xs text-muted-foreground">
                  Bakiye: <strong className="font-mono">{formatUsd(activeOrg?.credit_balance ?? 0, 2)}</strong>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Hedef Site (opsiyonel)</CardTitle>
            <CardDescription>
              Şimdi atarsan "Hazır" olduğunda tek tıkla gönderebilirsin.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <select
              value={form.site_id ?? ''}
              onChange={(e) => update('site_id', e.target.value || null)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">Henüz seçme</option>
              {sites.data?.sites.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name} ({s.site_url.replace(/^https?:\/\//, '')})
                </option>
              ))}
            </select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Görsel Seçenekleri
            </CardTitle>
            <CardDescription>
              Metinle beraber AI görseller de üretilsin mi?
              {selectedTemplate?.has_image_prompt ? (
                <>
                  {' '}
                  <span className="text-primary font-medium">
                    "{selectedTemplate.label}" şablonunun hazır görsel promptu var — otomatik kullanılacak.
                  </span>
                </>
              ) : (
                ' Seçili şablonun hazır görsel promptu yok; genel bir prompt kullanılır.'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <label className="flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer hover:bg-accent/50">
              <input
                type="checkbox"
                checked={imgOpts.enable_featured}
                onChange={(e) => setImgOpts((p) => ({ ...p, enable_featured: e.target.checked }))}
                className="h-4 w-4 rounded border-input mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">Öne çıkan görsel (featured image)</div>
                <div className="text-xs text-muted-foreground">
                  Yazının başında kullanılır, WordPress'e dispatch edilirken featured olarak yüklenir.
                </div>
              </div>
              {imgOpts.enable_featured && imageCreditsPerImage > 0 && (
                <span className="text-xs text-muted-foreground whitespace-nowrap font-mono">
                  {formatUsd(imageCreditsPerImage)}
                </span>
              )}
            </label>

            <label className="flex items-start gap-3 rounded-md border px-3 py-2.5 cursor-pointer hover:bg-accent/50">
              <input
                type="checkbox"
                checked={imgOpts.enable_body}
                onChange={(e) => setImgOpts((p) => ({ ...p, enable_body: e.target.checked }))}
                className="h-4 w-4 rounded border-input mt-0.5"
              />
              <div className="flex-1">
                <div className="text-sm font-medium">Yazı içine görsel ekle</div>
                <div className="text-xs text-muted-foreground">
                  Makaledeki H2 bölümlerine figure + caption olarak yerleştirilir.
                </div>
              </div>
              {imgOpts.enable_body && imageCreditsPerImage > 0 && (
                <span className="text-xs text-muted-foreground whitespace-nowrap font-mono">
                  {formatUsd(imageCreditsPerImage * imgOpts.body_count)}
                </span>
              )}
            </label>

            {imgOpts.enable_body && (
              <div className="ml-7 border-l-2 border-primary/30 pl-4 space-y-3">
                <div className="space-y-2">
                  <Label>Adet</Label>
                  <div className="flex gap-1.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setImgOpts((p) => ({ ...p, body_count: n as 1 | 2 | 3 | 4 | 5 }))}
                        className={cn(
                          'w-9 h-9 rounded-md border text-sm font-medium',
                          imgOpts.body_count === n
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'hover:bg-accent',
                        )}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Yerleşim (yazı akışındaki hizalama)</Label>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                    {(
                      [
                        { id: 'full_width', label: 'Tam satır', note: 'Blok olarak tek başına' },
                        { id: 'float_left', label: 'Sol', note: 'Yazı sağa akar' },
                        { id: 'float_right', label: 'Sağ', note: 'Yazı sola akar' },
                        { id: 'alternating', label: 'Karışık', note: 'Tek sol, çift sağ' },
                      ] as const
                    ).map((opt) => {
                      const active = imgOpts.body_layout === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setImgOpts((p) => ({ ...p, body_layout: opt.id }))}
                          className={cn(
                            'rounded-md border px-2 py-2.5 transition-colors text-center',
                            active
                              ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
                              : 'border-input hover:border-primary/60',
                          )}
                        >
                          <LayoutPreview layout={opt.id} active={active} />
                          <div className="text-xs font-medium mt-1.5">{opt.label}</div>
                          <div className="text-[10px] text-muted-foreground leading-tight">{opt.note}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {(imgOpts.enable_featured || imgOpts.enable_body) && (
              <div className="space-y-2">
                <Label>Görsel Modeli</Label>
                <ProviderModelSelect
                  groups={imagePricingGroups}
                  value={imgOpts.pricing_id}
                  onChange={(id) => setImgOpts((p) => ({ ...p, pricing_id: id }))}
                  providerLabel="Sağlayıcı"
                  modelLabel="Model"
                />
                {selectedImagePricing?.note && (
                  <p className="text-xs text-muted-foreground">{selectedImagePricing.note}</p>
                )}
              </div>
            )}

            {totalImageCredits > 0 && (
              <div className="rounded-md border border-primary/30 bg-primary/5 px-3 py-2 text-sm flex items-center gap-2">
                <Coins className="w-4 h-4 text-primary" />
                <span>
                  Toplam görsel: <strong className="font-mono">{formatUsd(totalImageCredits)}</strong>
                </span>
                <span className="text-xs text-muted-foreground ml-auto">
                  + metin üretim ücreti (ayrı)
                </span>
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Ek Talimat (opsiyonel)</CardTitle>
          </CardHeader>
          <CardContent>
            <textarea
              rows={3}
              value={form.custom_instructions ?? ''}
              onChange={(e) => update('custom_instructions', e.target.value)}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              placeholder="Örn: CTA'da rezervasyon butonundan bahset, ürün ismi olarak 'X marka' kullan..."
            />
          </CardContent>
        </Card>

        {error && (
          <div className="text-sm text-destructive bg-destructive/10 rounded-md border border-destructive/30 px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex items-center gap-2">
          <Button type="submit" disabled={createMut.isPending || generating}>
            <Sparkles className="w-4 h-4" />
            {generating ? 'AI çalışıyor…' : createMut.isPending ? 'Hazırlanıyor…' : 'Oluştur ve Üret'}
          </Button>
          <Button type="button" variant="outline" asChild>
            <Link to="/content">İptal</Link>
          </Button>
        </div>
      </form>
    </div>
  );
}

/** Mini-preview of how the image sits in the article body. Drawn with
 *  concrete Tailwind colors (zinc/neutral) — opacity-modified variables
 *  like `bg-primary/70` were rendering empty on some builds. Uses inline
 *  style for line widths to keep them visible regardless of JIT purge. */
function LayoutPreview({
  layout,
  active,
}: {
  layout: 'full_width' | 'float_left' | 'float_right' | 'alternating';
  active: boolean;
}) {
  const imgClass = active ? 'bg-blue-600 dark:bg-blue-500' : 'bg-zinc-500 dark:bg-zinc-400';
  const lineClass = active ? 'bg-blue-300 dark:bg-blue-800' : 'bg-zinc-300 dark:bg-zinc-700';

  const line = (widthPct: number) => (
    <div
      className={cn('h-[3px] rounded-sm', lineClass)}
      style={{ width: `${widthPct}%` }}
    />
  );

  if (layout === 'full_width') {
    return (
      <div className="mx-auto w-[92px] h-14 border border-border rounded p-1.5 space-y-1 bg-background">
        {line(100)}
        <div className={cn('h-4 rounded-sm', imgClass)} />
        {line(100)}
        {line(75)}
      </div>
    );
  }

  if (layout === 'float_left') {
    return (
      <div className="mx-auto w-[92px] h-14 border border-border rounded p-1.5 flex gap-1.5 bg-background">
        <div className={cn('w-6 h-full rounded-sm shrink-0', imgClass)} />
        <div className="flex-1 flex flex-col gap-1 justify-center">
          {line(100)}
          {line(100)}
          {line(85)}
          {line(70)}
        </div>
      </div>
    );
  }

  if (layout === 'float_right') {
    return (
      <div className="mx-auto w-[92px] h-14 border border-border rounded p-1.5 flex gap-1.5 bg-background">
        <div className="flex-1 flex flex-col gap-1 justify-center">
          {line(100)}
          {line(100)}
          {line(85)}
          {line(70)}
        </div>
        <div className={cn('w-6 h-full rounded-sm shrink-0', imgClass)} />
      </div>
    );
  }

  // alternating — two rows
  return (
    <div className="mx-auto w-[92px] h-14 border border-border rounded p-1 flex flex-col gap-1 bg-background">
      <div className="flex gap-1 flex-1">
        <div className={cn('w-4 h-full rounded-sm shrink-0', imgClass)} />
        <div className="flex-1 flex flex-col gap-[3px] justify-center">
          {line(100)}
          {line(75)}
        </div>
      </div>
      <div className="flex gap-1 flex-1">
        <div className="flex-1 flex flex-col gap-[3px] justify-center">
          {line(100)}
          {line(75)}
        </div>
        <div className={cn('w-4 h-full rounded-sm shrink-0', imgClass)} />
      </div>
    </div>
  );
}
